import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { tasksAPI, usersAPI } from '../../services/api';
import { useRole } from '../../hooks/useRole';

/**
 * SubtaskPanel
 * Props: taskId (string), onSubtaskChange (callback — triggers parent loadTask)
 */
export default function SubtaskPanel({ taskId, onSubtaskChange }) {
  const { isSupport } = useRole();

  const [subtasks,  setSubtasks]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [users,     setUsers]     = useState([]);
  const [toggling,  setToggling]  = useState({}); // { [subtaskId]: true }

  // Add-form state
  const [addTitle,      setAddTitle]      = useState('');
  const [addAssignedTo, setAddAssignedTo] = useState('');
  const [addDueDate,    setAddDueDate]    = useState('');
  const [adding,        setAdding]        = useState(false);

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const fetchSubtasks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await tasksAPI.getTask(taskId);
      setSubtasks(res.data?.data?.subtasks || []);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => { fetchSubtasks(); }, [fetchSubtasks]);

  useEffect(() => {
    usersAPI.getAll({ limit: 200 }).then(r => setUsers(r.data?.data?.users || [])).catch(() => {});
  }, []);

  // ── Add subtask ───────────────────────────────────────────────────────────
  const handleAdd = async (e) => {
    e.preventDefault();
    if (!addTitle.trim()) { toast.error('Title is required'); return; }
    setAdding(true);
    try {
      await tasksAPI.addSubtask(taskId, {
        title:       addTitle.trim(),
        assigned_to: addAssignedTo || undefined,
        due_date:    addDueDate    || undefined,
      });
      toast.success('Subtask added');
      setAddTitle(''); setAddAssignedTo(''); setAddDueDate('');
      await fetchSubtasks();
      onSubtaskChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to add subtask');
    } finally {
      setAdding(false);
    }
  };

  // ── Toggle complete ───────────────────────────────────────────────────────
  const handleToggle = async (subtask) => {
    setToggling(prev => ({ ...prev, [subtask._id]: true }));
    try {
      if (subtask.status === 'Done') {
        await tasksAPI.updateSubtask(taskId, subtask._id, { status: 'To Do' });
      } else {
        await tasksAPI.completeSubtask(taskId, subtask._id);
      }
      await fetchSubtasks();
      onSubtaskChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to update subtask');
    } finally {
      setToggling(prev => ({ ...prev, [subtask._id]: false }));
    }
  };

  // ── Delete subtask ────────────────────────────────────────────────────────
  const handleDelete = async (subtask) => {
    if (!window.confirm(`Delete "${subtask.title}"?`)) return;
    try {
      await tasksAPI.deleteSubtask(taskId, subtask._id);
      toast.success('Subtask deleted');
      await fetchSubtasks();
      onSubtaskChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to delete subtask');
    }
  };

  // ── Derived ───────────────────────────────────────────────────────────────
  const doneCount = subtasks.filter(s => s.status === 'Done').length;
  const total     = subtasks.length;
  const pct       = total > 0 ? Math.round((doneCount / total) * 100) : 0;

  const inputStyle = {
    padding: '7px 10px', borderRadius: 7,
    border: '1.5px solid #e2e8f0', fontSize: 13, color: '#1e293b',
    outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
    background: '#fff',
  };
  const labelStyle = { fontSize: 12, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 4 };

  return (
    <div>
      {/* ── Progress bar ──────────────────────────────────────────────────── */}
      {total > 0 && (
        <div style={{
          background: '#f8fafc', borderRadius: 10, padding: '12px 16px',
          marginBottom: 20, border: '1px solid #f1f5f9',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>
              {doneCount} of {total} subtask{total !== 1 ? 's' : ''} complete
            </span>
            <span style={{
              fontSize: 11, fontWeight: 700,
              color: pct === 100 ? '#16a34a' : '#1B2A6B',
              background: pct === 100 ? '#dcfce7' : '#e0e7ff',
              borderRadius: 4, padding: '1px 7px',
            }}>
              {pct}%
            </span>
          </div>
          <div style={{ height: 8, background: '#e2e8f0', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{
              height: '100%', width: `${pct}%`,
              background: pct === 100 ? '#22c55e' : '#1B2A6B',
              borderRadius: 4, transition: 'width 0.3s',
            }} />
          </div>
        </div>
      )}

      {/* ── Add subtask form ──────────────────────────────────────────────── */}
      {!isSupport && (
        <form onSubmit={handleAdd} style={{
          background: '#fff', borderRadius: 10,
          border: '1.5px solid #e2e8f0', padding: '14px 16px',
          marginBottom: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#1B2A6B', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span>➕</span> Add Subtask
          </div>

          {/* Title */}
          <div style={{ marginBottom: 10 }}>
            <input
              style={{ ...inputStyle, width: '100%' }}
              placeholder="Subtask title…"
              value={addTitle}
              onChange={e => setAddTitle(e.target.value)}
            />
          </div>

          {/* Assigned To + Due Date */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
            <div>
              <label style={labelStyle}>Assigned To</label>
              <select style={{ ...inputStyle, width: '100%' }} value={addAssignedTo} onChange={e => setAddAssignedTo(e.target.value)}>
                <option value="">— Unassigned —</option>
                {users.map(u => <option key={u._id} value={u._id}>{u.name}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Due Date</label>
              <input type="date" style={{ ...inputStyle, width: '100%' }} value={addDueDate} onChange={e => setAddDueDate(e.target.value)} />
            </div>
          </div>

          <button type="submit" disabled={adding}
            style={{
              padding: '8px 20px', borderRadius: 8, border: 'none',
              background: adding ? '#94a3b8' : '#1B2A6B',
              color: '#fff', fontWeight: 700, fontSize: 13,
              cursor: adding ? 'not-allowed' : 'pointer',
            }}>
            {adding ? 'Adding…' : '+ Add Subtask'}
          </button>
        </form>
      )}

      {/* ── Subtask list ──────────────────────────────────────────────────── */}
      {loading ? (
        <div style={{ color: '#94a3b8', fontSize: 13, padding: '12px 0' }}>Loading subtasks…</div>
      ) : subtasks.length === 0 ? (
        <div style={{
          background: '#f8fafc', borderRadius: 10,
          border: '1.5px dashed #e2e8f0', padding: '32px 24px',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>✅</div>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b', marginBottom: 6 }}>No subtasks yet</div>
          <div style={{ fontSize: 13, color: '#64748b' }}>Break this task into smaller steps.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {subtasks.map(s => {
            const isDone    = s.status === 'Done';
            const due       = s.due_date ? new Date(s.due_date) : null;
            const isOverdue = due && !isDone && due < new Date();
            return (
              <div
                key={s._id}
                style={{
                  background: isDone ? '#f0fdf4' : '#fff',
                  borderRadius: 8, padding: '10px 14px',
                  border: `1px solid ${isDone ? '#bbf7d0' : '#f1f5f9'}`,
                  display: 'flex', alignItems: 'center', gap: 10,
                  transition: 'background 0.15s',
                }}
              >
                {/* Checkbox */}
                <button
                  onClick={() => handleToggle(s)}
                  disabled={toggling[s._id]}
                  title={isDone ? 'Mark incomplete' : 'Mark complete'}
                  style={{
                    width: 22, height: 22, borderRadius: 5, flexShrink: 0,
                    border: `2px solid ${isDone ? '#22c55e' : '#cbd5e1'}`,
                    background: isDone ? '#22c55e' : '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: toggling[s._id] ? 'wait' : 'pointer',
                    fontSize: 13, color: '#fff', fontWeight: 700,
                    padding: 0, transition: 'all 0.15s',
                  }}
                >
                  {isDone ? '✓' : ''}
                </button>

                {/* Title + meta */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 13, fontWeight: isDone ? 500 : 600,
                    color: isDone ? '#94a3b8' : '#1e293b',
                    textDecoration: isDone ? 'line-through' : 'none',
                    lineHeight: 1.4,
                  }}>
                    {s.title}
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                    {s.assigned_to?.name && (
                      <span style={{
                        fontSize: 11, color: '#64748b',
                        display: 'flex', alignItems: 'center', gap: 3,
                      }}>
                        <span style={{
                          width: 16, height: 16, borderRadius: '50%',
                          background: '#1B2A6B', color: '#C9A84C',
                          display: 'inline-flex', alignItems: 'center',
                          justifyContent: 'center', fontSize: 9, fontWeight: 700,
                        }}>
                          {s.assigned_to.name[0].toUpperCase()}
                        </span>
                        {s.assigned_to.name}
                      </span>
                    )}
                    {due && (
                      <span style={{
                        fontSize: 11,
                        color: isOverdue ? '#dc2626' : '#64748b',
                        background: isOverdue ? '#fee2e2' : '#f1f5f9',
                        borderRadius: 4, padding: '1px 5px',
                        fontWeight: isOverdue ? 700 : 400,
                      }}>
                        📅 {due.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                      </span>
                    )}
                  </div>
                </div>

                {/* Delete */}
                {!isSupport && (
                  <button
                    onClick={() => handleDelete(s)}
                    title="Delete subtask"
                    style={{
                      padding: '4px 8px', borderRadius: 6,
                      border: '1px solid #fee2e2', background: '#fff',
                      cursor: 'pointer', fontSize: 11,
                      color: '#dc2626', fontWeight: 600, flexShrink: 0,
                    }}
                  >
                    🗑
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
